const DEFAULT_CONFIG = {
  autoplay: true,
  speed: 3000,
  direction: 'fade',
  pauseOnHover: true
}

const TRANSITION_DURATION = 300
const FADE_TRANSITION_DURATION = 500
const TOUCH_SWIPE_THRESHOLD = 50

const layoutHandlers = {
  horizontal: (slider, items) => {
    items[0].classList.add('topbar-item--active')
  },

  fade: (slider, items) => {
    slider.style.position = 'relative'

    items.forEach((item, index) => {
      Object.assign(item.style, {
        position: 'absolute',
        top: '0',
        left: '0',
        width: '100%',
        transition: 'opacity 0.5s ease-in-out',
        opacity: index === 0 ? '1' : '0',
        zIndex: index === 0 ? '10' : '1',
        pointerEvents: index === 0 ? 'auto' : 'none'
      })
    })
  },

  vertical: (slider, items) => {
    items[0].classList.add('topbar-item--active')
  }
}

const slideTransitionHandler = (slider, items, currentIndex, newIndex) => {
  items[currentIndex].classList.remove('topbar-item--active')
  items[currentIndex].classList.add('topbar-item--transitioning')

  setTimeout(() => {
    items[currentIndex].classList.remove('topbar-item--transitioning')
    items[newIndex].classList.add('topbar-item--active')
  }, TRANSITION_DURATION)
}

const updateHandlers = {
  horizontal: slideTransitionHandler,

  fade: (slider, items, currentIndex, newIndex) => {
    Object.assign(items[currentIndex].style, {
      opacity: '0',
      zIndex: '1',
      pointerEvents: 'none'
    })

    Object.assign(items[newIndex].style, {
      opacity: '1',
      zIndex: '10',
      pointerEvents: 'auto'
    })
  },

  vertical: slideTransitionHandler
}

const createTouchHandler = (direction, onNext, onPrev) => {
  let startY = 0
  let startX = 0
  let isDragging = false

  const threshold = TOUCH_SWIPE_THRESHOLD

  return {
    start: (e) => {
      startY = e.touches[0].clientY
      startX = e.touches[0].clientX
      isDragging = true
    },

    move: (e) => {
      if (!isDragging) return
      e.preventDefault()
    },

    end: (e) => {
      if (!isDragging) return
      isDragging = false

      const endY = e.changedTouches[0].clientY
      const endX = e.changedTouches[0].clientX
      const diffY = startY - endY
      const diffX = startX - endX

      if (direction === 'horizontal' || direction === 'fade') {
        if (Math.abs(diffX) > threshold) {
          if (diffX > 0) {
            onNext()
          } else {
            onPrev()
          }
        }
      } else {
        if (Math.abs(diffY) > threshold) {
          if (diffY > 0) {
            onNext()
          } else {
            onPrev()
          }
        }
      }
    }
  }
}

const createTopbarCarousel = (element) => {
  if (!element) return null

  const slider = element.querySelector('.topbar-slider')
  const items = Array.from(element.querySelectorAll('.topbar-item'))

  if (!slider || items.length === 0) return null

  const config = {
    autoplay: element.dataset.autoplay === 'true',
    speed: parseInt(element.dataset.speed) || DEFAULT_CONFIG.speed,
    direction: element.dataset.direction || DEFAULT_CONFIG.direction,
    pauseOnHover: element.dataset.pauseHover === 'true'
  }

  let currentIndex = 0
  let isTransitioning = false
  let autoplayTimer = null

  const next = () => {
    if (isTransitioning || items.length <= 1) return

    isTransitioning = true
    const nextIndex = (currentIndex + 1) % items.length

    updateSlide(nextIndex)

    setTimeout(() => {
      isTransitioning = false
    }, FADE_TRANSITION_DURATION)
  }

  const prev = () => {
    if (isTransitioning || items.length <= 1) return

    isTransitioning = true
    const prevIndex = (currentIndex - 1 + items.length) % items.length

    updateSlide(prevIndex)

    setTimeout(() => {
      isTransitioning = false
    }, FADE_TRANSITION_DURATION)
  }

  const updateSlide = (newIndex) => {
    const handler = updateHandlers[config.direction]
    if (handler) {
      handler(slider, items, currentIndex, newIndex)
    }
    currentIndex = newIndex
  }

  const startAutoplay = () => {
    if (items.length <= 1 || !config.autoplay) return

    autoplayTimer = setInterval(next, config.speed)
  }

  const pauseAutoplay = () => {
    if (autoplayTimer) {
      clearInterval(autoplayTimer)
      autoplayTimer = null
    }
  }

  const resumeAutoplay = () => {
    if (config.autoplay && !autoplayTimer) {
      startAutoplay()
    }
  }

  const setupLayout = () => {
    const handler = layoutHandlers[config.direction]
    if (handler) {
      handler(slider, items)
    }
  }

  const setupEventListeners = () => {
    if (config.pauseOnHover) {
      element.addEventListener('mouseenter', pauseAutoplay)
      element.addEventListener('mouseleave', resumeAutoplay)
    }
  }

  let touchHandler = null

  const setupTouchEvents = () => {
    touchHandler = createTouchHandler(config.direction, next, prev)

    element.addEventListener('touchstart', touchHandler.start)
    element.addEventListener('touchmove', touchHandler.move)
    element.addEventListener('touchend', touchHandler.end)
  }

  const init = () => {
    setupLayout()
    setupEventListeners()
    setupTouchEvents()

    if (config.autoplay && items.length > 1) {
      startAutoplay()
    }
  }

  return {
    init,
    next,
    prev,
    destroy: () => {
      pauseAutoplay()
      element.removeEventListener('mouseenter', pauseAutoplay)
      element.removeEventListener('mouseleave', resumeAutoplay)

      if (touchHandler) {
        element.removeEventListener('touchstart', touchHandler.start)
        element.removeEventListener('touchmove', touchHandler.move)
        element.removeEventListener('touchend', touchHandler.end)
        touchHandler = null
      }
    }
  }
}

const initTopbarCarousels = () => {
  const topbars = document.querySelectorAll('.topbar[data-autoplay]')

  if (!topbars || topbars.length === 0) return

  topbars.forEach(topbar => {
    try {
      const carousel = createTopbarCarousel(topbar)
      if (carousel) {
        carousel.init()
      }
    } catch (error) {
      console.warn('Failed to initialize topbar carousel:', error)
    }
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTopbarCarousels)
} else {
  initTopbarCarousels()
}
